// frontend/src/lib/rbac.js — RBAC-07 viewer model (the PRESENTATION half).
//
// Derives the role/scope view ONCE from the live /api/auth/me (hydrate.js calls
// setViewer(viewerFromMe(r))) and re-derives on switch-workspace. The screens read
// window.RBAC.* + window.MAV.viewer. THIS IS UX ONLY — the server (require-role +
// the §6 DEFINER fns) stays the authority; a hidden button is never "secure".
//
// Casing tripwire (F1): one /me response mixes cases — me.user.* is snake_case
// (workspace_id, pod_id) while me.memberships[].* is camelCase (workspaceId). The
// adapter reads both.
import { ROLE_MATRIX } from '../../../shared/role-matrix.js';

let _viewer = null;

// Server /me shape → viewer shape (the truth-table is the RBAC-07 ticket §"Viewer
// derivation truth-table"). Pure + side-effect-free so it unit-tests directly.
export function viewerFromMe(me) {
  const m = me || {};
  const memberships = Array.isArray(m.memberships) ? m.memberships : [];
  // ORG-01: "led" = workspaces the viewer MANAGES — team_lead or admin seats. A
  // multi-geo admin holds admin seats in several workspaces and gets the same
  // fan-out/switcher treatment a lead does (buildsPlan/contextWorkspaces unchanged).
  const led = memberships.filter((x) => x.role === 'team_lead' || x.role === 'admin');
  const orgTier = m.orgTier != null ? m.orgTier : null;
  const tier = orgTier != null ? 'org' : (led.length > 0 ? 'lead' : 'builder');
  const everySme = memberships.length > 0 && memberships.every((x) => x.role === 'sme');
  return {
    tier, orgTier,
    ctx: m.activeWorkspaceId || (memberships[0] && memberships[0].workspaceId) || null,  // default = single ws (F8: org/all are opt-in only)
    activeWorkspaceId: m.activeWorkspaceId || (memberships[0] && memberships[0].workspaceId) || null,  // the session's real ws — survives a 'all'/'org' ctx so the fan-out restores it
    led, seatWorkspaces: memberships, memberships,
    isOrg: tier === 'org', isLead: tier === 'lead', isBuilder: tier === 'builder',
    canSeeAdminDesk: orgTier != null || memberships.some((x) => x.role === 'admin'),  // PM tier, or an admin seat anywhere (ORG-01)
    canChangeTier: orgTier === 'product_manager',      // ORG-01: PM-only (admin is a seat now)
    canManageMembers: orgTier != null || led.length > 0,  // PM, or a team_lead/admin seat
    canStartBuild: !everySme,                          // an SME-only seat can't start builds (was QA)
    user: m.user || null,
  };
}

export function setViewer(v) { _seedEnginesFrom(v); _viewer = v; emit(); }
export function viewer() { return _viewer; }
// the switcher mutates only the active context (single ws id | 'all' | 'org')
export function setContext(ctx) { if (_viewer) { _viewer = { ..._viewer, ctx }; emit(); } }

function emit() {
  try {
    if (typeof window !== 'undefined') {
      if (window.MAV) window.MAV.viewer = _viewer;   // keep the global viewer current for the screens
      window.dispatchEvent(new CustomEvent('goose-rbac-change', { detail: _viewer }));
    }
  } catch (_) { /* SSR/test */ }
}

// React hook — subscribe to viewer/context changes + re-render. App subscribes too
// (so the whole tree reacts to a switch); components may use this directly as well.
export function useViewer() {
  const [, force] = window.React.useState(0);
  window.React.useEffect(() => {
    const h = () => force((n) => n + 1);
    window.addEventListener('goose-rbac-change', h);
    return () => window.removeEventListener('goose-rbac-change', h);
  }, []);
  return _viewer;
}

// The caller's effective role for the active workspace context. The PM tier outranks
// the seat; otherwise the seat role in the active ws (fallback: first seat) — an
// admin seat resolves here like any other seat (ORG-01: ws-scoped by construction).
export function roleForContext(v) {
  v = v || _viewer; if (!v) return null;
  if (v.orgTier === 'product_manager') return 'product_manager';
  const seat = (v.memberships || []).find((m) => m.workspaceId === v.ctx) || (v.memberships || [])[0];
  return seat ? seat.role : null;
}

// Boolean gate — mirrors the server ROLE_MATRIX via the ONE shared module (F15).
export function requireRole(action, v) {
  const allowed = ROLE_MATRIX[action];
  if (!allowed) return false;
  const role = roleForContext(v);
  return role != null && allowed.includes(role);
}

export function isAggregateView(v) { v = v || _viewer; return !!v && (v.ctx === 'org' || v.ctx === 'all'); }

// THE build-load routing decision (A5 leak guard), extracted pure so it is
// node-testable AND is the one source loadContextBuilds branches on:
//   'org'    → the audited /api/org/builds ONLY (never a relaxed tenant query)
//   'fanout' → single-tenant per-workspace union (a lead; NEVER /api/org/builds)
//   'single' → the tenant builds for the active workspace
// It NEVER mixes — that crossover IS the cross-tenant leak this campaign prevents.
export function buildsPlan(v) {
  v = v || _viewer;
  if (!v) return { source: 'none', workspaces: [] };
  if (v.ctx === 'org') return { source: 'org', workspaces: [] };
  if (v.ctx === 'all') return { source: 'fanout', workspaces: (v.led && v.led.length ? v.led : (v.memberships || [])).map((m) => m.workspaceId) };
  return { source: 'single', workspaces: v.ctx ? [v.ctx] : [] };
}

// Workspace IDs the active context covers. 'org' → a sentinel meaning "use
// /api/org/builds" (NEVER a relaxed tenant query); does NOT filter a jobs array.
export function contextWorkspaces(v) {
  v = v || _viewer; if (!v) return [];
  if (v.ctx === 'org') return ['__ORG__'];
  if (v.ctx === 'all') return (v.led.length ? v.led : v.memberships).map((m) => m.workspaceId);
  return v.ctx ? [v.ctx] : [];
}

// ── VD-01 / VD-03 · per-workspace default build engine (PY-44's FE seam) ──────────
// The engine a workspace's new builds default to. It GATES the dashboard's meters
// (codex → the usage-quota surfaces of VD-01; anything else → the $ spend meters) and
// prefills New Build's engine. codex is the org default — the live, sub-$0 engine.
//
// PY-44 (LANDED — the LIVE-WIRE stub is retired): `workspaces.default_engine` is a real
// column (db/022) and the server is authoritative. The map below is an in-memory MIRROR
// seeded from /api/auth/me's memberships (each carries defaultEngine; setViewer re-seeds on
// every hydrate + workspace switch, so the server value wins). setWorkspaceEngine() persists
// via PUT /api/admin/workspace-engine (admin/PM, active-workspace-scoped) — no localStorage.
const ORG_DEFAULT_ENGINE = 'codex';
let _wsEngines = {};

function _seedEnginesFrom(v) {
  _wsEngines = {};
  ((v && v.memberships) || []).forEach((m) => {
    if (m && m.workspaceId && m.defaultEngine) _wsEngines[m.workspaceId] = m.defaultEngine;
  });
}

export function engineForWorkspace(wsId) {
  return (wsId && _wsEngines[wsId]) || ORG_DEFAULT_ENGINE;
}

// VD-03's admin control writes here. OPTIMISTIC: the local map + broadcasts flip immediately
// (goose-rbac-change → dashboard re-derives activeEngine; goose-codex-usage → VD-01 panels
// re-read), then the PUT persists; on failure the previous value is restored + re-broadcast
// (the control visibly snaps back — honest, no fake-saved state). Uses window.GooseAPI at
// CLICK time (never at module load — the data.js load-order lesson); absent (SSR/unit) →
// local-only, same surface as before.
export function setWorkspaceEngine(wsId, engine) {
  if (!wsId || !engine) return;
  const prev = _wsEngines[wsId];
  _wsEngines[wsId] = engine;
  _emitEngineChange();
  const API = (typeof window !== 'undefined' && window.GooseAPI) || null;
  if (!API || !API.setWorkspaceEngine) return;
  return API.setWorkspaceEngine(wsId, engine).catch(() => {
    if (prev === undefined) delete _wsEngines[wsId]; else _wsEngines[wsId] = prev;
    _emitEngineChange();
  });
}

function _emitEngineChange() {
  emit();
  try { if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('goose-codex-usage')); } catch (_) { /* SSR/test */ }
}

// The engine governing the active dashboard context (VD-01). codex → quota surfaces
// replace the $ meters; a single non-codex ws → that engine; a multi-ws span with
// differing engines → 'mixed' (keeps the $ meters); org context → the org default.
export function activeEngine(v) {
  v = v || _viewer; if (!v) return ORG_DEFAULT_ENGINE;
  if (v.ctx === 'org') return ORG_DEFAULT_ENGINE;
  const wsIds = contextWorkspaces(v).filter((id) => id && id !== '__ORG__');
  if (!wsIds.length) return ORG_DEFAULT_ENGINE;
  const engines = [...new Set(wsIds.map(engineForWorkspace))];
  return engines.length === 1 ? engines[0] : 'mixed';
}

// The workspaces VD-03's selector offers, each with its resolved defaultEngine. Admin/PM
// → org-wide (all teams from MAV.pods — the workspace list until PY-44's endpoint lands);
// others → their own seats (read-only in the control anyway). Names resolve via MAV.pods;
// a bare id is the honest fallback if MAV lacks the team.
export function engineWorkspaces(v) {
  v = v || _viewer;
  const MAV = (typeof window !== 'undefined' && window.MAV) || null;
  const pods = (MAV && Array.isArray(MAV.pods)) ? MAV.pods : [];
  let src;
  if (v && v.orgTier) src = pods;
  else if (v) {
    const ids = new Set((v.memberships || []).map((m) => m.workspaceId));
    src = pods.filter((p) => ids.has(p.id));
    if (!src.length) src = (v.memberships || []).map((m) => ({ id: m.workspaceId, name: m.workspaceId }));
  } else src = pods;
  return src.map((p) => ({ id: p.id, name: p.name || p.id, defaultEngine: engineForWorkspace(p.id) }));
}

if (typeof window !== 'undefined') {
  window.RBAC = { viewerFromMe, setViewer, viewer, setContext, useViewer, requireRole, roleForContext, isAggregateView, contextWorkspaces, buildsPlan, ROLE_MATRIX, engineForWorkspace, setWorkspaceEngine, activeEngine, engineWorkspaces, ORG_DEFAULT_ENGINE };
}
